import { creerClient, type Client } from '../api/client.js';
import { creerFetchMocke, sessionDepuisAdresse } from '../mocks/fetch-mocke.js';
import { Controleur, type EnvControleur } from './controleur.js';
import { lireEnLigne } from './hors-ligne.js';
import type { InvitationInstallation } from './pwa.js';

/** Forme minimale de `window` utilisée par la coquille : de quoi l’éprouver sans navigateur. */
export interface FenetreMinimale extends EventTarget {
  location: { pathname: string; search: string };
  history: {
    pushState: (etat: unknown, titre: string, url: string) => void;
    replaceState: (etat: unknown, titre: string, url: string) => void;
  };
  innerWidth: number;
  navigator: { onLine: boolean };
}

export function creerEnvNavigateur(win: FenetreMinimale, client: Client): EnvControleur {
  return {
    client,
    chemin: () => `${win.location.pathname}${win.location.search}`,
    pousser: (chemin) => win.history.pushState({}, '', chemin),
    remplacer: (chemin) => win.history.replaceState({}, '', chemin),
    largeur: () => win.innerWidth,
    enLigne: () => lireEnLigne(win.navigator),
  };
}

type Cible = Pick<Controleur, 'surChangementAdresse' | 'surRedimension' | 'surReseau' | 'surInstallationProposee' | 'surInstallee'>;

/** Branche les événements du navigateur sur le contrôleur ; renvoie la fonction qui les débranche. */
export function brancherEvenements(win: FenetreMinimale, ctrl: Cible): () => void {
  const ecouteurs: [string, EventListener][] = [
    ['popstate', () => void ctrl.surChangementAdresse()],
    ['resize', () => ctrl.surRedimension(win.innerWidth)],
    ['online', () => ctrl.surReseau(true)],
    ['offline', () => ctrl.surReseau(false)],
    [
      'beforeinstallprompt',
      (e) => {
        e.preventDefault();
        ctrl.surInstallationProposee(e as unknown as InvitationInstallation);
      },
    ],
    ['appinstalled', () => ctrl.surInstallee()],
  ];
  for (const [type, ecoute] of ecouteurs) win.addEventListener(type, ecoute);
  return () => {
    for (const [type, ecoute] of ecouteurs) win.removeEventListener(type, ecoute);
  };
}

export type FenetreNavigateur = FenetreMinimale & { fetch: typeof fetch };

/**
 * Contrôleur branché sur le navigateur. Avec `mocks`, aucune requête ne quitte la page : les réponses viennent des
 * exemples du contrat, et `?role=` (avec `&incarne=`) ouvre la session fictive d’un rôle pour parcourir son espace.
 */
export function creerControleurNavigateur(win: FenetreNavigateur, options: { mocks?: boolean } = {}): Controleur {
  const fetchUtilise: typeof fetch = options.mocks
    ? creerFetchMocke({ session: sessionDepuisAdresse(win.location.search) })
    : win.fetch.bind(win);
  return new Controleur(creerEnvNavigateur(win, creerClient({ fetch: fetchUtilise })));
}
